import { FC, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink } from "react-router";
import classNames from "classnames";
import OutsideClickHandler from "../../../OutsideClickHandler/OutsideClickHandler";

import { selectAuthIsMember, selectAuthIsManagedClient, selectAuthIsSuperuser, selectAuthUser } from "../../../../store/selectors/auth";
import { getStripeCustomerPortal, logoutUser } from "../../../../store/auth/slice";
import { ERoutes } from "../../../../constants/routes";
import Avatar from "../../../Avatar/Avatar";
import { MenuItem } from "./types";
import Skeleton from "../../../Skeleton/Skeleton";


import styles from "./Setting.module.css";



const Setting: FC = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const ignoreRef = useRef<HTMLButtonElement>(null);
  const user = useSelector(selectAuthUser);
  const isMember = useSelector(selectAuthIsMember);
  const isManagedClient = useSelector(selectAuthIsManagedClient);
  const isSuperuser = useSelector(selectAuthIsSuperuser);


  if (!user) return <Skeleton width="48px" height="48px" />;

  const userFullName = user.user_metadata.full_name;

  const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
    const target = e.target as Node | null;
    if (ignoreRef.current && target && ignoreRef.current.contains(target)) {
      return;
    }
    setIsOpen(false);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const renderMenuItemContent = (item: MenuItem) => {
    switch (true) {
      case Boolean(item.to && !item.target):
        return (
          <NavLink
            className={({ isActive }) => isActive ? styles["is-active"] : undefined}
            to={item.to!}
            end
          >
            {item.label}
          </NavLink>
        );
      case Boolean(item.to && item.target):
        return <Link to={item.to!} target={item.target}>{item.label}</Link>;
      case Boolean(item.href):
        return <a href={item.href} target={item.target}>{item.label}</a>;
      case Boolean(item.onClick):
        return <button onClick={item.onClick}>{item.label}</button>;
      default:
        return <span>{item.label}</span>;
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: "Profile Settings",
      to: ERoutes.Settings,
    },
    {
      label: 'Add member',
      to: ERoutes.SettingsAddMember
    },
    {
      label: 'API Keys',
      to: ERoutes.SettingsApiKeys
    },
    ...(isMember || isManagedClient
      ? []
      : [
        {
          label: 'Payment Info',
          onClick: () => dispatch(getStripeCustomerPortal()),
        },
      ]),
    ...(isSuperuser ? [{
      label: 'Superuser',
      to: ERoutes.Superuser,
    }] : []),
    {
      label: 'Log Out',
      onClick: handleLogout,
      isLogout: true
    }
  ];

  const renderMenuBurger = () => {
    return (
      <button
        ref={ignoreRef}
        className={styles["setting__avatar"]}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open settings menu"
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <div
            className={classNames(styles["setting__burger"], isOpen && styles["is-open"])}
            onClick={() => setIsOpen(!isOpen)}
            role="button"
            aria-label="Close settings menu"
          >
            {Array(3).fill(null).map((_, index) => <span key={index}></span>)}
          </div>
        ) : (
          <Avatar size="md" />
        )}
      </button>
    );
  };

  return (
    <div className={styles["setting"]} role="navigation">
      <div className={styles["setting__names"]}>
        <p>{userFullName}</p>
        {renderMenuBurger()}
      </div>

      {isOpen && (
        <OutsideClickHandler onOutsideClick={handleOutsideClick}>
          <div
            className={styles["setting__menu"]}
            role="menu"
            aria-label="Settings menu"
          >
            <ul>
              {menuItems.map((item) => (
                <li
                  key={item.label}
                  className={classNames(
                    styles["setting__menu-item"],
                    item.isLogout && styles["setting__menu-item--logout"]
                  )}
                >
                  {renderMenuItemContent(item)}
                </li>
              ))}
            </ul>
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

export default Setting;
